import {
  analysis,
  buffer,
  cursor,
  guesses,
  HINT_COST,
  hintPurchasedAt,
  marksByGuess,
  puzzle,
  revealingGuessIndex,
  shakeNonce,
  SPACES_COST,
  spacesPurchased,
  spacesPurchasedAt,
  status,
} from "../game/state";
import { Row } from "./Row";

export function Board() {
  const submitted = guesses.value;
  const submittedMarks = marksByGuess.value;
  const isPlaying = status.value === "playing";
  const a = analysis.value;
  const p = puzzle.value;
  // No puzzle loaded → nothing to render. BoardScroll's skeleton path
  // covers the loading state; this branch is defensive against any
  // future caller that mounts <Board /> directly.
  if (p == null) return null;
  const totalRows = p.maxGuesses;
  const purchasedAt = hintPurchasedAt.value;
  const spacesAt = spacesPurchasedAt.value;
  const revealingIdx = revealingGuessIndex.value;
  const blank = new Array(a.letterCount).fill(null);

  // Slot-walk in final-board-layout order. Each `*PurchasedAt` is the
  // top index of that reveal's contiguous block; the walk emits paid
  // rows first when the slot falls inside a block, otherwise the next
  // pending guess (submitted or active), otherwise a blank filler.
  // Mirrors the same walk in `web/src/game/share.ts` and
  // `web/src/game/playthrough.ts` so all three layouts agree.
  const slots: (preact.JSX.Element | null)[] = [];
  let g = 0;
  for (let s = 0; s < totalRows; s++) {
    if (purchasedAt != null && s >= purchasedAt && s < purchasedAt + HINT_COST) {
      slots.push(
        <Row key={`p-h-${s}`} analysis={a} letters={blank} paid="hint" />,
      );
      continue;
    }
    if (spacesAt != null && s >= spacesAt && s < spacesAt + SPACES_COST) {
      slots.push(
        <Row key={`p-s-${s}`} analysis={a} letters={blank} paid="spaces" />,
      );
      continue;
    }
    if (g < submitted.length) {
      const isReveal = g === revealingIdx;
      slots.push(
        <Row
          key={`g-${g}`}
          analysis={a}
          letters={submitted[g]!}
          marks={submittedMarks[g]}
          revealing={isReveal}
          previousLetters={g > 0 ? submitted[g - 1]! : null}
        />,
      );
      g++;
      continue;
    }
    // While a row is mid-reveal, suppress the active row entirely: no
    // carry-forward greens, no cursor — the player can't enter their
    // next guess until the flip-reveal commits.
    if (isPlaying && revealingIdx == null && g === submitted.length) {
      slots.push(
        <Row
          key="active"
          analysis={a}
          letters={buffer.value}
          cursorLetterIndex={cursor.value}
          currentRow
          shakeNonce={shakeNonce.value}
        />,
      );
      g++;
      continue;
    }
    slots.push(<Row key={`f-${s}`} analysis={a} letters={blank} />);
  }

  // `board--bare` collapses every `.sep--space` / `.sep--punct` so the
  // letter cells run flush together until Spaces is bought, at which
  // point the modifier drops and the structured mask snaps in-place.
  const cls = `board${spacesPurchased.value ? "" : " board--bare"}`;
  return <div class={cls}>{slots}</div>;
}
